import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Users, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", dob: "" });
  const [is18, setIs18] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!is18) return toast.error("You must confirm you are 18 or older.");
    setLoading(true);
    try {
      await register({ ...form, is_18_plus: is18 });
      toast.success("Account created! Let's verify you.");
      navigate("/onboarding");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-5 py-12 bg-sand">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Users className="w-10 h-10 text-terracotta mx-auto" />
          <h1 className="font-display text-4xl font-bold text-charcoal mt-3">Join Saathly</h1>
          <p className="text-charcoal/60 mt-2">Verified people. Public places. Real conversations.</p>
        </div>
        <form onSubmit={submit} className="bg-white rounded-2xl border border-border p-8 shadow-sm space-y-5">
          <div>
            <Label className="text-charcoal font-semibold">Full name</Label>
            <Input data-testid="reg-name" required value={form.name} onChange={set("name")}
              className="mt-1.5 rounded-xl focus-visible:ring-terracotta/50" placeholder="Your name" />
          </div>
          <div>
            <Label className="text-charcoal font-semibold">Email</Label>
            <Input data-testid="reg-email" type="email" required value={form.email} onChange={set("email")}
              className="mt-1.5 rounded-xl focus-visible:ring-terracotta/50" placeholder="you@email.com" />
          </div>
          <div>
            <Label className="text-charcoal font-semibold">Password</Label>
            <Input data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={set("password")}
              className="mt-1.5 rounded-xl focus-visible:ring-terracotta/50" placeholder="At least 6 characters" />
          </div>
          <div>
            <Label className="text-charcoal font-semibold">Date of birth</Label>
            <Input data-testid="reg-dob" type="date" required value={form.dob} onChange={set("dob")}
              className="mt-1.5 rounded-xl focus-visible:ring-terracotta/50" />
          </div>
          <label className="flex items-start gap-3 bg-sand rounded-xl p-4 cursor-pointer">
            <Checkbox data-testid="reg-18plus" checked={is18} onCheckedChange={setIs18} className="mt-0.5 data-[state=checked]:bg-terracotta data-[state=checked]:border-terracotta" />
            <span className="text-sm text-charcoal/80 leading-relaxed"><strong>I am 18 years or older.</strong> I agree to the <Link to="/legal/terms" className="text-terracotta underline">Terms</Link>, <Link to="/legal/privacy" className="text-terracotta underline">Privacy Policy</Link> and the strictly platonic, public-venue-only rules.</span>
          </label>
          <Button data-testid="reg-submit" type="submit" disabled={loading}
            className="w-full rounded-full bg-terracotta hover:bg-terracotta/90 text-white h-11">
            {loading ? "Creating account..." : "Create account"}
          </Button>
          <div className="flex items-center gap-3 py-1">
            <div className="h-px bg-border flex-1" /><span className="text-xs text-charcoal/40">or</span><div className="h-px bg-border flex-1" />
          </div>
          <button type="button" data-testid="google-login" onClick={googleLogin}
            className="w-full h-11 rounded-full border border-border bg-white hover:bg-sand flex items-center justify-center gap-2 font-semibold text-charcoal transition-colors duration-200">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" /> Continue with Google
          </button>
          <p className="text-xs text-charcoal/50 text-center">Google sign-ups still confirm 18+ and complete verification during onboarding.</p>
          <p className="flex items-center justify-center gap-1.5 text-xs text-charcoal/50"><ShieldCheck className="w-4 h-4 text-sage" /> Verified, encrypted & DPDP-aligned</p>
          <p className="text-center text-sm text-charcoal/60">Already a member? <Link to="/login" className="text-terracotta font-semibold">Log in</Link></p>
        </form>
      </div>
    </div>
  );
}
